import api from './api';

export interface Mcp {
  tipo: string;
  mcp: string;
  cartera?: number | null;
  grupo?: number | null;
  descripcion?: string | null;
  clase?: string | null;
  nroCuenta?: number | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  montoSujeto?: number | null;
  montoRetencion?: number | null;
  porcentaje?: number | null;
  idImpuestoAfip?: string | null;
  idRegulacion?: string | null;
  idEscala?: string | null;
  idRetencion?: string | null;
  montoSujetoMax?: number | null;
  montoRetencionMax?: number | null;
  reporteRpt?: string | null;
  codRetIg?: string | null;
  montoMinNoImpon?: number | null;
  porcNoInsc?: number | null;
  porcPersHumSucInd?: number | null;
  idJurisdiccion?: string | null;
  idRegimen?: string | null;
  /** Marca el MCP como medio de redondeo de la caja/grupo. */
  esRedondeo?: boolean;
  /**
   * Máximo absoluto de redondeo (decimal).
   * Sirve para faltante (+) o excedente (−) en recibo y orden de pago.
   */
  limiteRedondeo?: number | null;
}

export const MCP_TIPOS: { value: string; label: string }[] = [
  { value: 'E', label: 'E — Efectivo' },
  { value: 'T', label: 'T — Tarjeta' },
  { value: 'C', label: 'C — Cheque' },
  { value: 'TB', label: 'TB — Transferencia bancaria' },
  { value: 'O', label: 'O — Otros' },
  { value: 'P', label: 'P — Cheque propio' },
  { value: 'R', label: 'R — Retención' },
];

export interface McpSearchParams {
  searchTerm?: string;
  tipo?: string;
  /** Id numérico de Grupo MCP (opcional). */
  grupo?: number;
  /** Filtro por MCPs marcados como medio de redondeo. */
  esRedondeo?: boolean;
  pageNumber: number;
  pageSize: number;
}

export interface IMcpPagedResult {
  items: Mcp[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const enc = (s: string) => encodeURIComponent(s);

const mcpService = {
  getMcps: async (params: McpSearchParams): Promise<IMcpPagedResult> => {
    const q: Record<string, string | number | boolean> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
    };
    const st = params.searchTerm?.trim();
    if (st) q.SearchTerm = st;
    const tp = params.tipo?.trim();
    if (tp) q.Tipo = tp;
    if (params.grupo != null && Number.isFinite(params.grupo)) q.Grupo = params.grupo;
    if (typeof params.esRedondeo === 'boolean') q.EsRedondeo = params.esRedondeo;
    const response = await api.get<IMcpPagedResult>('/api/Mcps', { params: q });
    return response.data;
  },

  getAllMcps: async (
    params?: Pick<McpSearchParams, 'searchTerm' | 'tipo' | 'grupo' | 'esRedondeo'>,
  ): Promise<Mcp[]> => {
    const pageSize = 50;
    const all: Mcp[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await mcpService.getMcps({
        pageNumber,
        pageSize,
        searchTerm: params?.searchTerm,
        tipo: params?.tipo,
        grupo: params?.grupo,
        esRedondeo: params?.esRedondeo,
      });
      all.push(...response.items);
      totalPages = response.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    return all;
  },

  /** MCPs de un grupo, opcionalmente solo los de redondeo (`GET /api/Mcps/por-grupo/{grupo}`). */
  getMcpsPorGrupo: async (
    grupo: number,
    opts?: { esRedondeo?: boolean; pageNumber?: number; pageSize?: number },
  ): Promise<IMcpPagedResult> => {
    const response = await api.get<IMcpPagedResult>(`/api/Mcps/por-grupo/${grupo}`, {
      params: {
        esRedondeo: opts?.esRedondeo,
        pageNumber: opts?.pageNumber ?? 1,
        pageSize: opts?.pageSize ?? 100,
      },
    });
    return response.data;
  },

  /** Lista MCP de redondeo del grupo/caja. */
  listarMcpsRedondeo: async (grupo: number): Promise<Mcp[]> => {
    try {
      const byGrupo = await mcpService.getMcpsPorGrupo(grupo, { esRedondeo: true, pageSize: 100 });
      if (byGrupo.items?.length) return byGrupo.items;
    } catch {
      /* fallback al listado general */
    }
    return mcpService.getAllMcps({ grupo, esRedondeo: true });
  },

  getMcp: async (mcp: string, tipo: string): Promise<Mcp> => {
    const response = await api.get<Mcp>(`/api/Mcps/${enc(mcp.trim())}/${enc(tipo.trim())}`);
    return response.data;
  },

  createMcp: async (dto: Mcp): Promise<Mcp> => {
    const response = await api.post<Mcp>('/api/Mcps', dto);
    return response.data;
  },

  updateMcp: async (mcp: string, tipo: string, dto: Mcp): Promise<void> => {
    const m = mcp.trim();
    const t = tipo.trim();
    await api.put(`/api/Mcps/${enc(m)}/${enc(t)}`, { ...dto, mcp: m, tipo: t });
  },

  updateMcpGrupo: async (dto: Mcp, grupo: number | null): Promise<void> => {
    await mcpService.updateMcp(dto.mcp, dto.tipo, { ...dto, grupo });
  },

  puedeEliminar: async (mcp: string, tipo: string) => {
    const response = await api.get<{ canDelete: boolean; motivos?: string[]; message?: string }>(
      `/api/Mcps/${enc(mcp.trim())}/${enc(tipo.trim())}/puede-eliminar`
    );
    return response.data;
  },

  deleteMcp: async (mcp: string, tipo: string): Promise<void> => {
    await api.delete(`/api/Mcps/${enc(mcp.trim())}/${enc(tipo.trim())}`);
  },

  /**
   * Asegura el MCP "Redondeo" (tipo O) para el grupo.
   * Si no existe, el backend lo crea copiando parámetros del efectivo indicado.
   */
  asegurarRedondeo: async (grupo: number, mcpEfectivo?: string): Promise<Mcp> => {
    const params: Record<string, string | number> = { grupo };
    const efectivo = mcpEfectivo?.trim();
    if (efectivo) params.mcpEfectivo = efectivo;
    const response = await api.post<Mcp>('/api/Mcps/asegurar-redondeo', null, { params });
    return response.data;
  },
};

export default mcpService;
